import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, tap } from 'rxjs';
import * as fromGenerated from '../../../_generated';
import * as fromShared from '../../shared';
import { chatsFeature, ChatsState } from '../../store/chats.reducer';
import { ImageOverlayViewerComponent } from './image-overlay-viewer.component';

/**
 * Smart wrapper for the image overlay viewer.
 *
 * Resolves the image URL for the dumb overlay with one rule for every source
 * (composer or thread): a **cached same-session preview blob**
 * (`ChatImagePreviewStore`) first, then the existing **presigned-URL** endpoint.
 * The blob lets a just-uploaded image render across the short ingestion
 * (`processing → ready`) gap where the presigned object isn't servable yet; a
 * rehydrated chat with no local blob falls back to presigned (the durable
 * source). The future in-thread thumbnail reuses this exact resolution.
 *
 * Mounted once inside `ConversationPage`, which provides the page-scoped
 * `ChatImagePreviewStore` (populated by the composer) that this reads.
 */
@Component({
  selector: 'squadbox-image-overlay-viewer-smart',
  standalone: true,
  imports: [ImageOverlayViewerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <squadbox-image-overlay-viewer
      [isVisible]="isOpen()"
      [src]="resolvedSrc()"
      [filename]="filename()"
      [loadError]="resolveError()"
      (closeClicked)="close()"
    />
  `,
})
export class ImageOverlayViewerSmartComponent {
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly documentsService = inject(fromGenerated.DocumentsService);
  private readonly imagePreview = inject(fromShared.ChatImagePreviewStore);

  private readonly imageOverlay = this.chatsStore.selectSignal(
    chatsFeature.selectImageOverlay,
  );

  public readonly isOpen = computed(() => this.imageOverlay().isOpen);
  public readonly filename = computed(
    () => this.imageOverlay().source?.filename ?? '',
  );
  public readonly resolvedSrc = signal<string | null>(null);
  /**
   * True when the URL could not be resolved (presigned fetch failed) — drives
   * the dumb component's error state, since a null `src` never fires
   * `<img> error`.
   */
  public readonly resolveError = signal(false);

  /**
   * Monotonic token: a presigned response only applies if it is still the
   * latest requested source, so a slow response can't overwrite a newer one.
   */
  private requestToken = 0;

  constructor() {
    // Resolve once per source change: the cached preview blob first (covers the
    // upload + ingestion window), else the presigned repository URL.
    effect(() => {
      const source = this.imageOverlay().source;
      const token = ++this.requestToken;
      this.resolveError.set(false);

      if (!source) {
        this.resolvedSrc.set(null);
        return;
      }

      const documentUuid =
        source.kind === 'chatMessage' ? source.documentUuid : source.uuid;

      const preview = this.imagePreview.getPreviewUrl(documentUuid);
      if (preview) {
        this.resolvedSrc.set(preview);
        return;
      }

      // No local blob (e.g. a rehydrated chat): fetch the presigned URL.
      this.resolvedSrc.set(null);
      this.documentsService
        .documentsControllerGeneratePresignedUrlV1(documentUuid)
        .pipe(
          tap((presigned) => {
            if (token === this.requestToken) {
              this.resolvedSrc.set(presigned.url);
            }
          }),
          catchError(() => {
            if (token === this.requestToken) {
              this.resolveError.set(true);
            }
            return EMPTY;
          }),
        )
        .subscribe();
    });
  }

  public close(): void {
    this.chatsStore.dispatch(
      fromShared.ImageOverlayActions.closeImageOverlay(),
    );
  }
}
